import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "429",
  "slug": "what-are-the-seven-seas",
  "title": "What Are the Seven Seas, Really?",
  "excerpt": "The phrase is ancient, poetic, and surprisingly slippery. Here is where the seven seas came from and which bodies of water the term means today.",
  "date": "2026-06-13",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1561728130-afd430af0493?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxvY2VhbiUyMHNhaWxpbmclMjBzaGlwfGVufDF8MHx8fDE3ODEzMzg1MTl8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "The seven seas has meant different things to different cultures across history. The modern interpretation divides the world ocean into seven: the North and South Atlantic, North and South Pacific, the Indian, Arctic, and Southern Oceans.",
  "content": [
    {
      "type": "paragraph",
      "text": "To sail the seven seas is one of the most evocative phrases in the language, conjuring tall ships and distant horizons. But ask which seven, exactly, and most people draw a blank. The honest answer is that the seven seas has never pointed to one fixed list. It is a phrase whose meaning has drifted across cultures and centuries, which is part of what makes it so interesting."
    },
    {
      "type": "heading",
      "text": "An Ancient and Shifting Phrase"
    },
    {
      "type": "paragraph",
      "text": "The idea of seven seas is genuinely old, far older than any modern map. Different civilisations each filled in their own seven depending on the waters they actually sailed. The number seven mattered as much as the specific seas, carrying a sense of completeness and the whole known world, the way we might say the four corners of the Earth without meaning literal corners. Seven was a number of wholeness in many ancient cultures, so seven seas simply meant all the seas there are."
    },
    {
      "type": "heading",
      "text": "What Earlier Cultures Meant"
    },
    {
      "type": "paragraph",
      "text": "For ancient Mediterranean sailors, the seven seas were the local waters they knew and traded across, the seas around southern Europe and the Near East. Medieval European and Arab traders had their own versions, often listing the seas along the great routes between Europe, Africa, and Asia that carried spices and silk. To a Persian or Arab merchant, the seven seas were the chain of waters on the long voyage east toward China."
    },
    {
      "type": "paragraph",
      "text": "In other words, the seven seas were always the seas that mattered to the people using the phrase. As the known world expanded, so did the list. The term was less a precise geography than a poetic shorthand for everywhere worth sailing, which is why no two old sources fully agree on the membership."
    },
    {
      "type": "heading",
      "text": "The Modern Seven Seas"
    },
    {
      "type": "paragraph",
      "text": "Today, when the phrase is used in a literal geographic sense, it usually refers to the division of the single global ocean into seven parts:"
    },
    {
      "type": "list",
      "items": [
        "The North Atlantic Ocean",
        "The South Atlantic Ocean",
        "The North Pacific Ocean",
        "The South Pacific Ocean",
        "The Indian Ocean",
        "The Arctic Ocean",
        "The Southern Ocean, around Antarctica"
      ]
    },
    {
      "type": "paragraph",
      "text": "Splitting the Atlantic and Pacific into northern and southern halves is what gets the count to seven. It is a tidy modern answer to an old question, even if no sailor ever needed to think of the Atlantic as two separate seas in practice."
    },
    {
      "type": "heading",
      "text": "Seven Seas or Five Oceans?"
    },
    {
      "type": "paragraph",
      "text": "You may also have learned that the world has five oceans: the Atlantic, Pacific, Indian, Arctic, and Southern. That list and the modern seven seas are really the same water counted two ways. The five-ocean model keeps the Atlantic and Pacific whole, while the seven-seas version splits each into north and south. The Southern Ocean around Antarctica is the newest addition to the official roster, only widely recognised in recent decades, which shows that even our formal map of the seas is still being edited."
    },
    {
      "type": "heading",
      "text": "One Ocean, Many Names"
    },
    {
      "type": "paragraph",
      "text": "There is a deeper truth hiding under the phrase. All of the world’s oceans are connected into a single continuous body of saltwater, sometimes called the world ocean. Every boundary we draw, between the Atlantic and the Pacific, or where the Southern Ocean begins, is a human convention rather than a wall in the water. The seven seas is really one sea that we have chosen to slice up for convenience and storytelling."
    },
    {
      "type": "heading",
      "text": "Why the Phrase Endures"
    },
    {
      "type": "paragraph",
      "text": "The seven seas survives because it was never meant to be a checklist. It captures the romance of a fully explored world, the satisfaction of completeness, and the human habit of carving the vast and continuous into nameable pieces. Whether you count five oceans or seven seas, you are looking at the same connected water that covers most of the planet."
    },
    {
      "type": "paragraph",
      "text": "From orbit, that single world ocean is unmistakable, a continuous blue wrapped around scattered land. If you enjoy reading coastlines and currents and working out which sea you are looking at, EarthGuessr will set you adrift on a satellite view and ask you to find your bearings."
    }
  ]
};
